import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flag, Loader2, ShieldAlert, ChevronLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const Reasons = ({ reasons, f, setF }) => (
  <div className="space-y-4" data-testid="report-reasons">
    {reasons.map((c, ci) => (
      <section key={c.id} className="space-y-2">
        <p className="text-sm font-semibold">{ci + 1}. {c.title}</p>
        {c.items.map((i) => {
          const on = f.category === c.id && f.reason === i.id;
          return (
            <button key={i.id} type="button" onClick={() => setF({ ...f, category: c.id, reason: i.id })} aria-pressed={on}
              data-testid={`report-reason-${i.id}`}
              className={`w-full text-left rounded-xl border p-3 transition-colors ${on ? "border-destructive bg-destructive/5 ring-1 ring-destructive" : "hover:border-destructive/50"}`}>
              <p className="text-sm font-medium">{i.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{i.desc}</p>
            </button>
          );
        })}
      </section>
    ))}
  </div>
);

const ReportForm = ({ data, reasons, businessId, onDone }) => {
  const [f, setF] = useState({ workshop_id: data.workshops[0]?.id, category: "", reason: "", description: "" });
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/reports", { business_id: businessId, ...f }); toast.success("Denuncia enviada. VIVOTaller revisará el caso."); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (step === 1) {
    return (
      <div className="space-y-4">
        {data.workshops.length > 1 && (
          <label className="block text-sm font-medium space-y-1"><span>¿Qué taller quieres denunciar?</span>
            <select className="w-full h-10 rounded-md border bg-background px-3 text-sm" value={f.workshop_id} onChange={(e) => setF({ ...f, workshop_id: e.target.value })} data-testid="report-workshop">
              {data.workshops.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select></label>
        )}
        <Reasons reasons={reasons} f={f} setF={setF} />
        <Button className="w-full" disabled={!f.reason} onClick={() => setStep(2)} data-testid="report-next">Continuar</Button>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <button type="button" onClick={() => setStep(1)} className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground" data-testid="report-back"><ChevronLeft className="w-3.5 h-3.5" />Cambiar motivo</button>
      <label className="block text-sm font-medium space-y-1"><span>Cuéntanos qué pasó (mínimo 20 caracteres)</span>
        <Textarea rows={5} maxLength={2000} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Describe con detalle lo ocurrido: fecha, sesión y lo que viviste." data-testid="report-description" /></label>
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-xs flex gap-2" data-testid="report-warning">
        <ShieldAlert className="w-4 h-4 text-destructive shrink-0" />
        <span>Al enviar, la cuenta del Maestro queda <strong>deshabilitada de inmediato</strong> (sus talleres se ocultan y sus sesiones se suspenden) hasta que VIVOTaller lo contacte. Tu identidad no se comparte con el Maestro. Usa esta opción solo para hechos reales.</span>
      </div>
      <Button variant="destructive" className="w-full gap-1.5" disabled={busy || f.description.trim().length < 20} onClick={send} data-testid="report-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}Enviar denuncia
      </Button>
    </div>
  );
};

export function ReportMaestroButton({ businessId, maestroName }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [reasons, setReasons] = useState([]);
  useEffect(() => {
    if (!open) return;
    setData(null);
    Promise.all([api.get(`/reports/eligibility/${businessId}`), api.get("/reports/reasons")])
      .then(([e, r]) => { setData(e.data); setReasons(r.data); })
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setOpen(false); });
  }, [open, businessId]);
  const click = () => {
    if (!user) { toast.info("Inicia sesión como alumno para denunciar a un Maestro"); navigate("/login"); return; }
    if (user.role !== "customer") { toast.info("Cambia a Modo Alumno para denunciar a un Maestro"); return; }
    setOpen(true);
  };
  return (
    <>
      <button type="button" onClick={click} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-destructive transition-colors" data-testid="report-maestro-btn">
        <Flag className="w-3.5 h-3.5" />Denunciar a este Maestro
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Flag className="w-5 h-5 text-destructive" />Denunciar a {maestroName}</DialogTitle>
            <DialogDescription>Tu denuncia ayuda a mantener una comunidad segura y confiable.</DialogDescription>
          </DialogHeader>
          {!data ? <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-8" /> : !data.eligible ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="report-not-eligible">{data.reason}</p>
          ) : <ReportForm data={data} reasons={reasons} businessId={businessId} onDone={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
